// applyCommand: the single entry point from UI/bots into the sim (docs/08 §2, §6).
// Pure: clones the input state, mutates the clone, returns it with the resulting events.
import { bonusInput } from './bonus/index';
import { deepClone } from './clone';
import { D } from './decimal';
import { say } from './dialogue';
import {
  continueRun,
  draftPick,
  draftReroll,
  draftSkip,
  reorderCharms,
  sellCharm,
  shopBuy,
  shopBuyHymn,
  shopReroll,
  shopService,
} from './intermission';
import { checkAchievements, idle } from './achievements';
import { acceptDeal, refuseDeal } from './deals';
import { answerPhone, chooseEnding, readFlyer, remember } from './ending';
import { passiveReel } from './grid';
import { feedCoins, feedSymbol, holdCost } from './hunger';
import { buyNode, buyRelic, buyRelicSlot, molt, newRun, slotRelic } from './meta';
import { computeModifiers, type Modifiers } from './modifiers';
import { bank, gambleAccept, gambleDecline, nudge, pull } from './spin';
import type { Command, CommandResult, GameEvent, GameState } from './types';
import { offerSelf, payNow } from './window';

export function toggleHold(state: GameState, mods: Modifiers, reel: number, events: GameEvent[]): string | undefined {
  const run = state.run;
  if (!run || (run.phase !== 'ready' && run.phase !== 'nudge')) return 'not ready';
  if (!mods.features.has('hold')) return 'hold locked';
  if (reel < 0 || reel >= run.held.length) return 'bad reel';
  if (!run.held[reel] && (mods.lockedReels.has(reel) || passiveReel(run, reel))) return 'reel locked';
  if (run.held[reel]) {
    run.held[reel] = false;
    run.charges.hold += run.holdPaid[reel] ?? 1;
    run.holdPaid[reel] = 0;
  } else {
    const cost = holdCost(run, mods);
    if (run.charges.hold < cost) return 'no charges';
    const wouldHold = run.held.filter(Boolean).length + 1;
    if (wouldHold >= run.held.length) return 'cannot hold every reel';
    run.held[reel] = true;
    run.charges.hold -= cost;
    run.holdPaid[reel] = cost;
    run.stats.holdsUsed++;
    say(state, 'hold', events);
  }
  events.push({ e: 'HOLD_CHANGED', held: [...run.held], charges: run.charges.hold });
  return undefined;
}

function dispatch(state: GameState, cmd: Command, events: GameEvent[]): string | undefined {
  const mods = computeModifiers(state.meta, state.run);
  switch (cmd.t) {
    case 'NEW_RUN':
      return newRun(state, events, cmd.size);
    case 'PULL':
      return pull(state, mods, cmd.perfect, events);
    case 'TOGGLE_HOLD':
      return toggleHold(state, mods, cmd.reel, events);
    case 'NUDGE':
      return nudge(state, mods, cmd.reel, cmd.dir, cmd.steps ?? 1, events);
    case 'COLLECT': {
      const run = state.run;
      if (!run || run.phase !== 'nudge') return 'nothing to collect';
      bank(state, mods, events);
      return undefined;
    }
    case 'PAY_NOW': {
      const run = state.run;
      if (run?.phase === 'nudge') bank(state, mods, events);
      return payNow(state, mods, events);
    }
    case 'DRAFT_PICK':
      return draftPick(state, cmd.index, events);
    case 'DRAFT_SKIP':
      return draftSkip(state, mods, events);
    case 'DRAFT_REROLL':
      return draftReroll(state, mods, events);
    case 'SHOP_BUY':
      return shopBuy(state, mods, cmd.slot, events);
    case 'SHOP_BUY_HYMN':
      return shopBuyHymn(state, mods, events);
    case 'SHOP_SERVICE':
      return shopService(state, mods, cmd.service, events);
    case 'SHOP_REROLL':
      return shopReroll(state, mods, events);
    case 'SELL_CHARM':
      return sellCharm(state, mods, cmd.index, events);
    case 'REORDER_CHARMS':
      return reorderCharms(state, cmd.order);
    case 'FEED_SYMBOL':
      return feedSymbol(state, mods, cmd.uid, events);
    case 'FEED_COINS':
      return feedCoins(state, mods, events);
    case 'CONTINUE':
      return continueRun(state, mods, events);
    case 'OFFER_SELF':
      return offerSelf(state, events);
    case 'BUY_NODE':
      return buyNode(state, cmd.id, events);
    case 'BONUS_INPUT':
      return bonusInput(state, mods, cmd.input, events);
    case 'DEAL_ACCEPT':
      return acceptDeal(state, mods, cmd.id, events);
    case 'DEAL_REFUSE':
      return refuseDeal(state, events);
    case 'GAMBLE_ACCEPT':
      return gambleAccept(state, mods, events);
    case 'GAMBLE_DECLINE':
      return gambleDecline(state, mods, events);
    case 'MOLT':
      return molt(state, events);
    case 'BUY_RELIC':
      return buyRelic(state, cmd.id, events);
    case 'SLOT_RELIC':
      return slotRelic(state, cmd.id, cmd.slot);
    case 'BUY_RELIC_SLOT':
      return buyRelicSlot(state);
    case 'SET_HAND':
      state.meta.hand = { ...state.meta.hand, ...cmd.hand };
      return undefined;
    case 'SET_RUN_SIZE':
      state.meta.runSize = cmd.size;
      return undefined;
    case 'KEEP_CHARM': {
      const run = state.run;
      if (!run || run.phase !== 'consumed' || run.flags.locketCharm === undefined) return 'no locket';
      if (cmd.index < 0 || cmd.index >= run.charms.length) return 'bad index';
      run.flags.locketCharm = cmd.index;
      return undefined;
    }
    case 'CHOOSE_ENDING':
      return chooseEnding(state, cmd.ending, events);
    case 'REMEMBER':
      return remember(state, mods, cmd.choice, events);
    case 'ANSWER_PHONE':
      return answerPhone(state, events);
    case 'READ_FLYER':
      return readFlyer(state, mods, events);
    case 'IDLE':
      return idle(state, cmd.minutes, events);
    case 'SET_SETTINGS':
      state.meta.settings = { ...state.meta.settings, ...cmd.settings };
      return undefined;
    case 'DEBUG': {
      if (cmd.action === 'souls') state.meta.souls += cmd.amount;
      else if (cmd.action === 'teeth') state.meta.teeth += cmd.amount;
      else if (cmd.action === 'tribute') {
        state.meta.lifetime.maxTribute = Math.max(state.meta.lifetime.maxTribute, cmd.amount);
        state.meta.cycleMaxTribute = Math.max(state.meta.cycleMaxTribute, cmd.amount);
      } else if (state.run && cmd.action === 'coins') state.run.coins = state.run.coins.add(D(cmd.amount));
      else if (state.run && cmd.action === 'spins') state.run.window.spinsLeft += cmd.amount;
      return undefined;
    }
  }
}

export function applyCommand(state: GameState, cmd: Command): CommandResult {
  const next = deepClone(state);
  const events: GameEvent[] = [];
  const error = dispatch(next, cmd, events);
  if (error) return { state, events: [], error };
  checkAchievements(state, next, cmd, events);
  return { state: next, events };
}
